import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '../supabase';
import { useSite } from '../context/SiteContext';
import {
  Search,
  Filter,
  Calendar,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ArrowDownUp,
  Receipt,
  X,
} from 'lucide-react';

// Buscador de ventas compartido por "Nueva devolución" y "Nueva nota de crédito".
// Antes cada pantalla tenía su propia búsqueda: exigía un término, topeaba en 20
// resultados y no permitía filtrar por fecha, sucursal ni estado de facturación.

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const toInputDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// created_at guarda la "hora forjada" del POS (hora local escrita como Z),
// así que el rango se arma en Z, igual que en VentasHistorial.
const toIsoStartOfDay = (s) => (s ? `${s}T00:00:00.000Z` : null);
const toIsoEndOfDay = (s) => (s ? `${s}T23:59:59.999Z` : null);

const currency = (n) =>
  Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const sanitizeTerm = (raw) => raw.replace(/[,()"']/g, ' ').trim();

// Solo un UUID real puede ir a `id.eq`: el formato de numero_factura
// ('0003-00000045') también es hexadecimal con guiones y Postgres devolvía 22P02.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ORDENES = [
  { value: 'fecha_desc', label: 'Fecha ↓ (más nuevas)', column: 'created_at', asc: false },
  { value: 'fecha_asc', label: 'Fecha ↑ (más viejas)', column: 'created_at', asc: true },
  { value: 'monto_desc', label: 'Monto ↓ (mayor)', column: 'total', asc: false },
  { value: 'monto_asc', label: 'Monto ↑ (menor)', column: 'total', asc: true },
];

const PERIODOS = [
  { key: 'hoy', label: 'Hoy' },
  { key: '7d', label: '7 días' },
  { key: '30d', label: '30 días' },
  { key: 'mes', label: 'Este mes' },
  { key: 'anio', label: 'Este año' },
  { key: 'todo', label: 'Todo el historial' },
];

const rangoPeriodo = (key) => {
  const hoy = new Date();
  switch (key) {
    case 'hoy':
      return [toInputDate(hoy), toInputDate(hoy)];
    case '7d': {
      const d = new Date();
      d.setDate(d.getDate() - 6);
      return [toInputDate(d), toInputDate(hoy)];
    }
    case '30d': {
      const d = new Date();
      d.setDate(d.getDate() - 29);
      return [toInputDate(d), toInputDate(hoy)];
    }
    case 'mes':
      return [toInputDate(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), toInputDate(hoy)];
    case 'anio':
      return [toInputDate(new Date(hoy.getFullYear(), 0, 1)), toInputDate(hoy)];
    case 'todo':
      return ['', ''];
    default:
      return [null, null];
  }
};

const DEFAULT_DESDE = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 29);
  return toInputDate(d);
})();

function BuscadorVentas({
  organizationId,
  selectColumns,
  onSelect,
  renderItem,
  placeholder = 'Buscar por cliente, N° de factura o ID...',
  vacioLabel = 'No hay ventas en el período seleccionado.',
}) {
  const { selectedSiteId } = useSite();

  const [loading, setLoading] = useState(true);
  const [ventas, setVentas] = useState([]);
  const [sitios, setSitios] = useState([]);

  const [busqueda, setBusqueda] = useState('');
  const [debouncedBusqueda, setDebouncedBusqueda] = useState('');
  const [fechaInicio, setFechaInicio] = useState(DEFAULT_DESDE);
  const [fechaFin, setFechaFin] = useState(toInputDate(new Date()));
  const [filtroSitio, setFiltroSitio] = useState(selectedSiteId || '');
  const [filtroFacturacion, setFiltroFacturacion] = useState('todas'); // todas | facturadas | sin_facturar
  const [montoMin, setMontoMin] = useState('');
  const [montoMax, setMontoMax] = useState('');
  const [orden, setOrden] = useState('fecha_desc');

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    setFiltroSitio(selectedSiteId || '');
    setPage(0);
  }, [selectedSiteId]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedBusqueda(busqueda);
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [busqueda]);

  useEffect(() => {
    if (!organizationId) return;
    let cancelado = false;
    supabase
      .from('sitios')
      .select('id, nombre')
      .eq('organization_id', organizationId)
      .order('nombre')
      .then(({ data }) => {
        if (!cancelado) setSitios(data || []);
      });
    return () => {
      cancelado = true;
    };
  }, [organizationId]);

  const resolveBusqueda = useCallback(async () => {
    const raw = debouncedBusqueda.trim();
    if (!raw) return null;
    if (UUID_RE.test(raw)) return `id.eq.${raw}`;
    const term = sanitizeTerm(raw);
    if (!term) return null;
    const like = `%${term}%`;
    const { data } = await supabase
      .from('clients')
      .select('id')
      .eq('organization_id', organizationId)
      .ilike('full_name', like)
      .limit(200);
    const partes = [`numero_factura.ilike.${like}`];
    const clientIds = (data || []).map((c) => c.id);
    if (clientIds.length) partes.push(`client_id.in.(${clientIds.join(',')})`);
    return partes.join(',');
  }, [organizationId, debouncedBusqueda]);

  const fetchVentas = useCallback(async () => {
    if (!organizationId) return;
    const reqId = ++reqIdRef.current;
    try {
      setLoading(true);
      const searchOr = await resolveBusqueda();
      const ordenCfg = ORDENES.find((o) => o.value === orden) || ORDENES[0];
      const from = page * pageSize;

      let q = supabase
        .from('sales')
        .select(selectColumns, { count: 'exact' })
        .eq('organization_id', organizationId)
        .eq('active', true)
        .neq('invoice_status', 'presupuesto');

      const desde = toIsoStartOfDay(fechaInicio);
      const hasta = toIsoEndOfDay(fechaFin);
      if (desde) q = q.gte('created_at', desde);
      if (hasta) q = q.lte('created_at', hasta);
      if (filtroSitio) q = q.eq('site_id', filtroSitio);
      if (filtroFacturacion === 'facturadas') q = q.not('cae', 'is', null);
      if (filtroFacturacion === 'sin_facturar') q = q.is('cae', null);
      if (montoMin !== '' && !Number.isNaN(Number(montoMin))) q = q.gte('total', Number(montoMin));
      if (montoMax !== '' && !Number.isNaN(Number(montoMax))) q = q.lte('total', Number(montoMax));
      if (searchOr) q = q.or(searchOr);

      const { data, error, count } = await q
        .order(ordenCfg.column, { ascending: ordenCfg.asc })
        .range(from, from + pageSize - 1);

      if (reqId !== reqIdRef.current) return; // llegó tarde: el usuario siguió filtrando
      if (error) throw error;
      setVentas(data || []);
      setTotalCount(count || 0);
    } catch (e) {
      console.error(e);
      if (reqId === reqIdRef.current) {
        setVentas([]);
        setTotalCount(0);
      }
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [
    organizationId,
    selectColumns,
    resolveBusqueda,
    orden,
    page,
    pageSize,
    fechaInicio,
    fechaFin,
    filtroSitio,
    filtroFacturacion,
    montoMin,
    montoMax,
  ]);

  useEffect(() => {
    fetchVentas();
  }, [fetchVentas]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const desdeFila = totalCount === 0 ? 0 : page * pageSize + 1;
  const hastaFila = Math.min(totalCount, (page + 1) * pageSize);

  useEffect(() => {
    if (page > 0 && page > totalPages - 1) setPage(0);
  }, [totalPages, page]);

  const setFiltro = (setter) => (valor) => {
    setter(valor);
    setPage(0);
  };

  const aplicarPeriodo = (key) => {
    const [ini, fin] = rangoPeriodo(key);
    if (ini === null) return;
    setFechaInicio(ini);
    setFechaFin(fin);
    setPage(0);
  };

  const periodoActivo = useMemo(
    () =>
      PERIODOS.find((p) => {
        const [i, f] = rangoPeriodo(p.key);
        return i === fechaInicio && f === fechaFin;
      })?.key || null,
    [fechaInicio, fechaFin]
  );

  const sinPeriodo = !fechaInicio && !fechaFin;

  const filtrosActivos =
    (filtroSitio ? 1 : 0) +
    (filtroFacturacion !== 'todas' ? 1 : 0) +
    (montoMin !== '' ? 1 : 0) +
    (montoMax !== '' ? 1 : 0) +
    (!sinPeriodo && periodoActivo !== '30d' ? 1 : 0);

  const limpiarFiltros = () => {
    setBusqueda('');
    setDebouncedBusqueda('');
    setFechaInicio(DEFAULT_DESDE);
    setFechaFin(toInputDate(new Date()));
    setFiltroSitio(selectedSiteId || '');
    setFiltroFacturacion('todas');
    setMontoMin('');
    setMontoMax('');
    setOrden('fecha_desc');
    setPage(0);
  };

  const inputCls =
    'w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-700 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30';
  const labelCls = 'text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 block';

  const filaDefault = (v) => (
    <button
      key={v.id}
      type="button"
      onClick={() => onSelect(v)}
      className="w-full text-left py-3 px-2 hover:bg-slate-50 rounded-lg transition-colors flex items-center justify-between"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="p-2 bg-blue-50 rounded-lg text-[#51A7FD] shrink-0">
          <Receipt size={14} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-black uppercase text-slate-800 tracking-tight truncate">
            {v.clients?.full_name || 'Consumidor Final'}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            {v.numero_factura || `#${String(v.id).slice(0, 8)}`} ·{' '}
            {v.fecha_venta || String(v.created_at).slice(0, 10)} · {v.sitios?.nombre || '—'}
          </p>
        </div>
      </div>
      <p className="text-sm font-black text-slate-700 shrink-0">$ {currency(v.total)}</p>
    </button>
  );

  return (
    <div>
      {/* Búsqueda + toggle de filtros */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
          <input
            type="text"
            placeholder={placeholder}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full pl-9 pr-9 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm font-bold text-slate-700 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
          />
          {busqueda && (
            <button
              type="button"
              onClick={() => setBusqueda('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setMostrarFiltros((v) => !v)}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-[#51A7FD]"
        >
          <Filter size={14} /> Filtros
          {filtrosActivos > 0 && (
            <span className="bg-[#51A7FD] text-white rounded-full px-1.5 py-0.5 text-[9px] leading-none">
              {filtrosActivos}
            </span>
          )}
          {mostrarFiltros ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {mostrarFiltros && (
        <div className="mt-4 p-4 bg-slate-50/60 border border-slate-100 rounded-2xl space-y-4">
          <div className="flex flex-wrap gap-2">
            {PERIODOS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => aplicarPeriodo(p.key)}
                className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${
                  periodoActivo === p.key
                    ? 'bg-[#51A7FD] text-white border-[#51A7FD]'
                    : 'bg-white text-slate-500 border-slate-100 hover:text-[#51A7FD]'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Desde</label>
              <div className="relative">
                <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
                <input
                  type="date"
                  value={fechaInicio}
                  max={fechaFin || undefined}
                  onChange={(e) => setFiltro(setFechaInicio)(e.target.value)}
                  className={`${inputCls} pl-9`}
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>Hasta</label>
              <div className="relative">
                <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
                <input
                  type="date"
                  value={fechaFin}
                  min={fechaInicio || undefined}
                  onChange={(e) => setFiltro(setFechaFin)(e.target.value)}
                  className={`${inputCls} pl-9`}
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>Sucursal</label>
              <select
                value={filtroSitio}
                onChange={(e) => setFiltro(setFiltroSitio)(e.target.value)}
                className={`${inputCls} uppercase`}
              >
                <option value="">Todas las sucursales</option>
                {sitios.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Facturación</label>
              <select
                value={filtroFacturacion}
                onChange={(e) => setFiltro(setFiltroFacturacion)(e.target.value)}
                className={`${inputCls} uppercase`}
              >
                <option value="todas">Todas</option>
                <option value="facturadas">Facturadas (con CAE)</option>
                <option value="sin_facturar">Sin facturar</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Monto</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  placeholder="Mín"
                  value={montoMin}
                  onChange={(e) => setFiltro(setMontoMin)(e.target.value)}
                  className={inputCls}
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Máx"
                  value={montoMax}
                  onChange={(e) => setFiltro(setMontoMax)(e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>Ordenar por</label>
              <div className="relative">
                <ArrowDownUp size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
                <select
                  value={orden}
                  onChange={(e) => setFiltro(setOrden)(e.target.value)}
                  className={`${inputCls} pl-9`}
                >
                  {ORDENES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {filtrosActivos > 0 && (
            <button
              type="button"
              onClick={limpiarFiltros}
              className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-[#D9534F] underline underline-offset-2"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      )}

      {/* Resultados */}
      <div className="mt-5">
        {loading ? (
          <div className="py-10 text-center text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 animate-pulse">
            Buscando...
          </div>
        ) : ventas.length === 0 ? (
          <div className="py-10 text-center">
            <Receipt size={28} className="mx-auto text-slate-200 mb-3" />
            <p className="text-xs font-black uppercase tracking-widest text-slate-400">Sin resultados</p>
            <p className="text-[10px] text-slate-400 mt-2 italic">{vacioLabel}</p>
            {!sinPeriodo && (
              <button
                type="button"
                onClick={() => aplicarPeriodo('todo')}
                className="mt-4 px-4 py-2 rounded-xl bg-slate-50 border border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-[#51A7FD]"
              >
                Buscar en todo el historial
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="divide-y divide-slate-100">
              {ventas.map((v) => (renderItem ? renderItem(v) : filaDefault(v)))}
            </div>

            <div className="mt-4 pt-3 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-[9px] font-bold uppercase tracking-widest text-slate-500">
                  {desdeFila}–{hastaFila} de {totalCount}
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(0);
                  }}
                  className="px-2 py-1 rounded-lg bg-white border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500 focus:outline-none"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n} / pág.
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                  className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-500 text-[10px] font-black uppercase tracking-widest hover:text-[#51A7FD] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <ChevronLeft size={12} /> Anterior
                </button>
                <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                  {page + 1}/{totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1 || loading}
                  className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-500 text-[10px] font-black uppercase tracking-widest hover:text-[#51A7FD] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  Siguiente <ChevronRight size={12} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default BuscadorVentas;
